import type { ServiceAccount } from '@compass/client/types/service_account';
import { useDeleteServiceAccount } from '@hooks/useServiceAccounts';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DeleteServiceAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceAccount: ServiceAccount;
}

export function DeleteServiceAccountDialog({
  open,
  onOpenChange,
  serviceAccount,
}: DeleteServiceAccountDialogProps) {
  const deleteMutation = useDeleteServiceAccount();

  const handleDelete = () => {
    if (!serviceAccount.id) return;
    deleteMutation.mutate(
      { service_account_id: serviceAccount.id },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) deleteMutation.reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Service Account</DialogTitle>
          <DialogDescription className="pt-2">
            Are you sure you want to delete{' '}
            <span className="font-medium text-foreground">{serviceAccount.name}</span>? This cannot
            be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            <li>Any automation using this credential will start returning 401.</li>
            <li>The record is preserved for audit but cannot be reactivated.</li>
          </ul>

          {deleteMutation.isError && (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">Failed to delete</p>
                  <p className="mt-1">
                    {getErrorMessage(deleteMutation.error) || 'Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
